const FRACTION_VALUES = { "¼": 0.25, "½": 0.5, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3, "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875 };

const normalize = (value, lang = "tr") => String(value || "")
  .trim()
  .toLocaleLowerCase(lang === "en" ? "en-US" : "tr-TR");

function parseQuantity(value) {
  const text = String(value || "").trim().replace(",", ".");
  if (FRACTION_VALUES[text] != null) return FRACTION_VALUES[text];
  const unicodeFraction = Object.keys(FRACTION_VALUES).find((key) => text.includes(key));
  const wholePart = Number.parseFloat(text.replace(/[¼½¾⅓⅔⅛⅜⅝⅞].*$/, "").trim()) || 0;
  if (unicodeFraction) return wholePart + FRACTION_VALUES[unicodeFraction];
  if (/^\d+\s+\d+\/\d+$/.test(text)) {
    const [whole, fraction] = text.split(/\s+/);
    const [top, bottom] = fraction.split("/").map(Number);
    return Number(whole) + (bottom ? top / bottom : 0);
  }
  if (/^\d+\/\d+$/.test(text)) {
    const [top, bottom] = text.split("/").map(Number);
    return bottom ? top / bottom : 0;
  }
  return Number.parseFloat(text);
}

function formatQuantity(value, lang = "tr") {
  const rounded = Math.max(0.125, Math.round(value * 8) / 8);
  const whole = Math.floor(rounded);
  const fraction = Math.round((rounded - whole) * 8) / 8;
  const fractionMap = { 0.125: "⅛", 0.25: "¼", 0.375: "⅜", 0.5: "½", 0.625: "⅝", 0.75: "¾", 0.875: "⅞" };
  if (fractionMap[fraction]) return `${whole || ""}${fractionMap[fraction]}`;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toLocaleString(lang === "en" ? "en-US" : "tr-TR", { maximumFractionDigits: 2 });
}

const QUANTITY_PATTERN = "(?:\\d+(?:[.,]\\d+)?(?:\\s+\\d+\\/\\d+)?|\\d+\\/\\d+|\\d*[¼½¾⅓⅔⅛⅜⅝⅞])";

export function getDefaultServingCount(servingText) {
  const numbers = String(servingText || "").match(/\d+/g)?.map(Number).filter(Number.isFinite) || [];
  if (!numbers.length) return 4;
  return Math.max(1, Math.min(12, Math.round(numbers.reduce((sum, value) => sum + value, 0) / numbers.length)));
}

export function scaleIngredientText(ingredient, targetServings, baseServings, lang = "tr") {
  const text = String(ingredient || "");
  const base = Number(baseServings) || 1;
  const target = Number(targetServings) || base;
  const ratio = target / base;
  if (Math.abs(ratio - 1) < 0.001) return text;

  const rangeRegex = new RegExp(`^(${QUANTITY_PATTERN})\\s*[-–]\\s*(${QUANTITY_PATTERN})(?=\\s|$)`);
  const rangeMatch = text.match(rangeRegex);
  if (rangeMatch) {
    const low = parseQuantity(rangeMatch[1]);
    const high = parseQuantity(rangeMatch[2]);
    if (Number.isFinite(low) && Number.isFinite(high)) {
      return text.replace(rangeRegex, `${formatQuantity(low * ratio, lang)}-${formatQuantity(high * ratio, lang)}`);
    }
  }

  const quantityRegex = new RegExp(`^(${QUANTITY_PATTERN})(?=\\s|x|×|$)`);
  const match = text.match(quantityRegex);
  if (!match) return text;
  const quantity = parseQuantity(match[1]);
  if (!Number.isFinite(quantity)) return text;
  return text.replace(quantityRegex, formatQuantity(quantity * ratio, lang));
}

function categoryForIngredient(name, lang) {
  const value = normalize(name, lang);
  const produce = lang === "en"
    ? ["onion", "garlic", "tomato", "pepper", "carrot", "potato", "herb", "parsley", "lemon", "vegetable", "fruit", "aubergine", "eggplant", "zucchini", "courgette", "cabbage", "broccoli", "cauliflower"]
    : ["soğan", "sarımsak", "domates", "biber", "havuç", "patates", "maydanoz", "limon", "sebze", "meyve", "patlıcan", "kabak", "lahana", "brokoli", "karnabahar"];
  const meat = lang === "en"
    ? ["beef", "lamb", "pork", "chicken", "turkey", "mince", "meat", "sausage", "bacon", "fish", "salmon", "shrimp"]
    : ["dana", "kuzu", "tavuk", "hindi", "kıyma", "et", "sucuk", "balık", "somon", "hamsi"];
  const deli = lang === "en" ? ["milk", "cheese", "yogurt", "butter", "cream", "egg"] : ["süt", "peynir", "yoğurt", "tereyağı", "krema", "yumurta"];
  const bakery = lang === "en" ? ["bread", "bun", "roll", "pastry", "tortilla"] : ["ekmek", "yufka", "lavaş", "pide"];
  if (produce.some((term) => value.includes(term))) return "Manav";
  if (meat.some((term) => value.includes(term))) return "Kasap";
  if (deli.some((term) => value.includes(term))) return "Şarküteri";
  if (bakery.some((term) => value.includes(term))) return "Fırın";
  return "Market";
}

export function ingredientToShoppingItem(ingredient, lang = "tr") {
  const original = String(ingredient || "").trim();
  if (!original) return null;
  const quantityRegex = new RegExp(`^(${QUANTITY_PATTERN})\\s*`);
  const quantityMatch = original.match(quantityRegex);
  const amount = quantityMatch?.[1] || "1";
  let remainder = quantityMatch ? original.slice(quantityMatch[0].length).trim() : original;

  const units = lang === "en"
    ? ["tablespoons?", "teaspoons?", "tbsp", "tsp", "cups?", "kilograms?", "grams?", "kg", "g", "litres?", "liters?", "ml", "l", "packs?", "cans?", "cloves?", "slices?", "rashers", "bunch(?:es)?", "pieces?"]
    : ["yemek kaşığı", "tatlı kaşığı", "çay kaşığı", "su bardağı", "kilogram", "gram", "kg", "g", "litre", "lt", "ml", "paket", "adet", "demet", "dilim", "diş"];
  const unitRegex = new RegExp(`^(${units.join("|")})(?=\\s|$)\\s*`, "i");
  const unitMatch = remainder.match(unitRegex);
  let unit = unitMatch?.[1] || (lang === "en" ? "pcs" : "adet");
  if (unitMatch) remainder = remainder.slice(unitMatch[0].length).trim();

  remainder = remainder
    .replace(/^[/|]\s*\d+(?:[.,]\d+)?\s*(?:cups?|tbsp|tsp|g|kg|ml|l)\s*/i, "")
    .replace(/^[x×]\s*/i, "")
    .replace(/^[-–,:]\s*/, "")
    .trim();

  if (!remainder || remainder.length < 2) {
    remainder = original;
    unit = lang === "en" ? "pcs" : "adet";
  }

  return {
    ad: remainder,
    miktar: amount,
    birim: unit,
    kategori: categoryForIngredient(remainder, lang),
    kaynakMetin: original,
  };
}
